import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link } from '../../app/router';
import { Button, Input, linkClass } from '../../ui';
import { AuthLayout, FormError } from './AuthLayout';
import { callbackUrl, emailSchema, newPasswordSchema } from './schemas';
import { useSession } from './session';
import { useCaptcha } from './Turnstile';

/** Signed out: asks for the reset email. Signed in (after the emailed link): sets a new password. */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const signedIn = session.status === 'signed-in';
  const captcha = useCaptcha();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = signedIn ? newPasswordSchema.safeParse(value) : emailSchema.safeParse(value);
    if (!parsed.success) return setError(signedIn ? t('auth.passwordTooShort') : t('auth.invalidEmail'));
    try {
      if (signedIn) await api.auth.updatePassword(parsed.data);
      else await api.auth.resetPassword(parsed.data, callbackUrl(href('reset')), captcha.token);
      setDone(true);
    } catch {
      setError(t('auth.genericError'));
      captcha.reset();
    }
  };

  if (done) {
    return (
      <AuthLayout title={signedIn ? t('auth.passwordSaved') : t('auth.checkEmail')}>
        {!signedIn && <p className="font-sans text-fg-muted">{t('auth.checkEmailBody', { email: value })}</p>}
        <Link className={linkClass} to={signedIn ? href('home') : href('login')}>
          {signedIn ? t('auth.continue') : t('auth.backToLogin')}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.resetTitle')}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {signedIn ? t('auth.newPassword') : t('auth.email')}
          <Input
            type={signedIn ? 'password' : 'email'}
            autoComplete={signedIn ? 'new-password' : 'email'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
        {!signedIn && captcha.widget}
        <FormError message={error} />
        <Button variant="primary" type="submit" disabled={!signedIn && !captcha.ready}>
          {signedIn ? t('auth.savePassword') : t('auth.sendResetLink')}
        </Button>
      </form>
    </AuthLayout>
  );
}
